import { type CallerInfo, lastConversations, type Me } from '@smarton-pbx/shared';
import type { CallInfo } from '@smarton-pbx/softphone';
import { parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js';
import { ExternalLink, UserPlus } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../api';
import { Dialog } from '../components/ui';
import { t } from '../i18n';
import { formatNumber } from '../lib/calls';
import { CallTimeline, ContactForm } from './contacts';
import { formatNextCall, StageName } from './pipeline/leads';

/**
 * On the call card: who the other party is (their contact card, their lead in
 * the pipeline), what the team should know, and the last calls with them with
 * what was said; or, for a number nobody has saved, a way to save it.
 */
export function CallerPanel({ me, call }: { me: Me; call: CallInfo }) {
  const [info, setInfo] = useState<CallerInfo | null>(null);
  const [saving, setSaving] = useState(false);
  const [version, setVersion] = useState(0);
  // Calls in: the PBX's call ID. Calls out: the number dialled, as the person's region reads it.
  const country = (me.region?.countryCode ?? 'LT') as CountryCode;
  const dialled = call.direction === 'outgoing' && call.remote.length > 5 ? (parsePhoneNumberFromString(call.remote, country)?.number ?? null) : null;
  const key = call.callId ?? dialled;

  useEffect(() => {
    setInfo(null);
    if (!key) return;
    let current = true;
    const load = call.callId ? api.callerInfo(call.callId) : api.numberInfo(dialled!);
    load.then(
      (result) => current && setInfo(result),
      () => undefined,
    );
    return () => {
      current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, version]);

  if (!info?.number.startsWith('+')) return null;
  const crm = info.crm?.customer ? info.crm : null;
  const shown = lastConversations(info.recent);
  const talked = shown.some((call) => call.outcome === 'ANSWERED' || call.summary);
  return (
    <div className="caller-panel" data-testid="caller-panel">
      {crm?.customer && (
        <div className="caller-contact" data-testid="caller-crm">
          <a href={crm.customer.url} target="_blank" rel="noopener noreferrer" className="plain-link">
            {crm.contact ? crm.contact.name : crm.customer.name} <ExternalLink size={12} aria-hidden />
          </a>
          {crm.contact && <span className="subtle"> · {crm.customer.name}</span>}
          <span className="subtle"> · SmartOn</span>
        </div>
      )}
      {info.contact ? (
        <>
          <div className="caller-contact">
            <Link to={`/contacts/${info.contact.id}`} className="plain-link" data-testid="caller-contact">
              {info.contact.name}
            </Link>
            {info.contact.company && <span className="subtle"> · {info.contact.company}</span>}
          </div>
          {info.contact.notes && <p className="caller-notes">{info.contact.notes}</p>}
        </>
      ) : (
        !crm &&
        !info.lead && (
          <button type="button" className="secondary small" onClick={() => setSaving(true)} data-testid="save-caller">
            <UserPlus size={14} aria-hidden /> {t('Save {number} as a contact', { number: formatNumber(info.number) })}
          </button>
        )
      )}
      {info.lead && (
        <>
          <div className="caller-contact caller-lead" data-testid="caller-lead">
            <Link to={`/pipeline?lead=${info.lead.id}`} className="plain-link">
              {info.lead.company ?? info.lead.name ?? t('Lead')}
            </Link>
            {info.lead.company && info.lead.name && (
              <span className="subtle" data-testid="caller-lead-person">
                {info.lead.position ? `${info.lead.name}, ${info.lead.position}` : info.lead.name}
              </span>
            )}
            <StageName stage={info.lead.stage} />
            {info.lead.followUpOn && (
              <span className="subtle">{t('next call {date}', { date: formatNextCall(info.lead.followUpOn, info.lead.followUpTime) })}</span>
            )}
          </div>
          {!info.contact && info.lead.notes && <p className="caller-notes">{info.lead.notes}</p>}
        </>
      )}
      {shown.length > 0 && (
        <div className="caller-recent">
          <div className="caller-recent-title">{talked ? t('Last conversations') : t('Last calls')}</div>
          <CallTimeline calls={shown} compact />
        </div>
      )}
      <Dialog open={saving} onClose={() => setSaving(false)} title={t('New contact')} wide>
        {saving && (
          <ContactForm
            me={me}
            contact={null}
            initialNumber={info.number}
            onCancel={() => setSaving(false)}
            onSaved={() => {
              setSaving(false);
              setVersion((v) => v + 1);
            }}
          />
        )}
      </Dialog>
    </div>
  );
}
